import type { MoneyValue } from '@akademi/site-builder-runtime';

/**
 * Prices for the home page's live blocks: an offer's unit price, or a kit's total.
 *
 * <p><strong>No tax arithmetic happens here.</strong> The backend sends every price with its
 * published figure and, where it could resolve the product's tax, the same price on the other
 * basis (`alternate`). A total is the sum of what the shopper would see line by line on each
 * basis, never a figure derived from the other with a rate typed into the browser. If one line
 * lacks the other basis, the total has none either — an honest "inc VAT" alone beats a guessed
 * "ex VAT".
 *
 * <p>Sums are done in whole pence. The backend's amounts carry four decimal places
 * ("103.2370"); each unit is rounded to the penny first, because that is the figure the shopper
 * can check on the product page, and float addition of the raw strings would drift.
 */

/** A price as the storefront API carries it: the runtime's money plus its other basis. */
export type LiveMoney = MoneyValue & { alternate?: { amount: string; taxInclusive: boolean } };

export interface LineTotal {
  currency: string;
  /** The published basis, in pence. */
  published: number;
  publishedInclusive: boolean;
  /** The other basis, in pence — absent unless every line carried it. */
  alternate?: number;
}

const DECIMAL = /^(-)?(\d+)(?:\.(\d*))?$/;

/** A decimal string as whole pence, rounded half-up. `null` when it is not a number. */
export function toMinor(amount: string | undefined | null): number | null {
  if (typeof amount !== 'string') return null;
  const match = DECIMAL.exec(amount.trim());
  if (!match) return null;
  const [, sign, whole, fraction = ''] = match;
  const digits = (fraction + '000').slice(0, 3);
  let minor = Number(whole) * 100 + Number(digits.slice(0, 2));
  if (Number(digits[2]) >= 5) minor += 1;
  return sign ? -minor : minor;
}

/** Pence as a currency string in the shop's locale. */
export function formatMinor(minor: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(minor / 100);
  } catch {
    return `${currency} ${(minor / 100).toFixed(2)}`;
  }
}

/**
 * The total of `lines` on both bases, or `null` when any line has no price, the currencies
 * disagree, or a line is published on a different basis from the others.
 */
export function totalOf(lines: Array<{ price: LiveMoney | undefined; quantity: number }>): LineTotal | null {
  if (lines.length === 0) return null;
  let currency: string | undefined;
  let inclusive: boolean | undefined;
  let published = 0;
  let alternate: number | undefined = 0;

  for (const { price, quantity } of lines) {
    if (!price || !Number.isInteger(quantity) || quantity <= 0) return null;
    const unit = toMinor(price.amount);
    if (unit === null) return null;
    if (currency !== undefined && price.currency !== currency) return null;
    if (inclusive !== undefined && !!price.taxInclusive !== inclusive) return null;
    currency = price.currency;
    inclusive = !!price.taxInclusive;
    published += unit * quantity;

    const other = price.alternate;
    const otherUnit = other && !!other.taxInclusive !== inclusive ? toMinor(other.amount) : null;
    alternate = alternate === undefined || otherUnit === null ? undefined : alternate + otherUnit * quantity;
  }

  return {
    currency: currency!,
    published,
    publishedInclusive: inclusive!,
    ...(alternate === undefined ? {} : { alternate }),
  };
}
